// The single upload path to api.video, shared by the initial-upload panel and
// the replace-file dialog so the two cannot drift apart.
//
// `onPlayable` fires once api.video has transcoded far enough to serve the
// video; its `assets` ({ hls, mp4, thumbnail }) are what the video record
// stores. It is a bonus, not a guarantee: the SDK starts its playable poll
// *without awaiting it* and returns immediately, so `upload()` always resolves
// first -- for every file, however small. Callers that waited on the callback
// alone therefore reported "no playable assets" on uploads that had in fact
// succeeded. The resolved value carries the same assets, so it is the source
// of truth and the callback is only an early hint.
import { VideoUploader as ApiVideoUploader } from '@api.video/video-uploader';

export const UPLOAD_TOKEN = 'to3YG1un8sNZOKAtWYQ3R7lp';

// The SDK uploads chunks strictly one at a time, awaiting each response before
// the next byte leaves the browser. At api.video's 5 MiB minimum a 7 GB master
// becomes ~1,400 sequential round-trips, and the per-chunk latency dominates
// the transfer entirely. 32 MiB cuts that to ~220 while keeping a failed chunk
// cheap enough to retry on a domestic uplink. (The SDK's own ceiling is
// 128 MiB.)
const CHUNK_SIZE = 32 * 1024 * 1024; // 32 MiB
const RETRIES = 5;

// api.video ids are alphanumeric and prefixed `vi`.
const VIDEO_ID = /^vi[A-Za-z0-9]+$/;

export function uploadVideoFile(file, { onProgress, onPlayable } = {}) {
  const uploader = new ApiVideoUploader({
    file,
    uploadToken: UPLOAD_TOKEN,
    chunkSize: CHUNK_SIZE,
    retries: RETRIES,
  });

  if (onProgress) {
    uploader.onProgress((event) => {
      onProgress(Math.round((event.uploadedBytes * 100) / event.totalBytes));
    });
  }
  if (onPlayable) uploader.onPlayable(onPlayable);

  return uploader.upload();
}

// api.video asset URLs embed the video id: .../vod/<videoId>/mp4/source.mp4
export function videoIdFromAssets(assets) {
  const match = /\/vod\/([^/]+)\//.exec(assets?.mp4 || assets?.hls || '');
  return match ? match[1] : null;
}

// Every asset of a video lives under /vod/<id>/, so the id alone names all of
// them. This is what lets a file uploaded straight to api.video be adopted by a
// record: the browser upload is not the only way in, and when it is the slow or
// broken way, it should not be the only way out.
//
// Accepts a bare id or any URL containing one, because what the dashboard puts
// on the clipboard depends on which panel you copied from.
export function assetsFromVideoId(input) {
  const raw = (input || '').trim();
  const id = /\/vod\/([^/?#]+)\//.exec(raw)?.[1] || raw;
  if (!VIDEO_ID.test(id)) return null;
  return {
    hls: `https://vod.api.video/vod/${id}/hls/manifest.m3u8`,
    mp4: `https://vod.api.video/vod/${id}/mp4/source.mp4`,
    thumbnail: `https://vod.api.video/vod/${id}/thumbnail.jpg`,
  };
}

// Is there actually a playable video behind these URLs?
//
// api.video answers the HLS manifest with 202, or a 200 with an empty body,
// while the video is still transcoding, and serves the real playlist only once
// it can play. That is the same signal the SDK's own poll watches, and it needs
// no API key -- which is why this check can live in the browser at all.
//
// It cannot distinguish "still working" from "processing failed": both stay at
// 202 forever. So `processing` is reported as a warning to check the dashboard,
// never as an error -- guessing which one it is here is exactly the mistake
// that left an upload looking successful for half an hour.
export async function probeAssets(assets) {
  let response;
  try {
    response = await fetch(assets.hls);
  } catch {
    // A blocked or offline fetch says nothing about the video.
    return 'unknown';
  }
  if (response.status === 404) return 'missing';
  if (response.status === 202) return 'processing';
  if (!response.ok) return 'unknown';
  return (await response.text()).trim().length ? 'ready' : 'processing';
}
